// PWA install detection & trigger. Chromium-family browsers (desktop and
// Android) fire `beforeinstallprompt`, which we capture and defer so the UI
// can trigger the native install dialog from a real button tap. Safari
// (iOS/iPadOS/macOS) never fires that event — there the only path is the
// manual Share-sheet gesture, so the UI falls back to written per-platform
// steps (see ui/installSheet.ts) when no native prompt is available.

export type Platform = 'ios' | 'android' | 'desktop';

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

let deferred: BeforeInstallPromptEvent | null = null;
const listeners = new Set<() => void>();

window.addEventListener('beforeinstallprompt', (e) => {
  e.preventDefault();
  deferred = e as BeforeInstallPromptEvent;
  for (const l of [...listeners]) l();
});

window.addEventListener('appinstalled', () => {
  deferred = null;
  for (const l of [...listeners]) l();
});

/** Whether the browser has handed us a native install prompt we can trigger now. */
export function canPromptInstall(): boolean {
  return deferred !== null;
}

/** Subscribe to changes in native-prompt availability. Returns an unsubscribe function. */
export function onInstallPromptChange(fn: () => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/** Trigger the native install dialog. Resolves true if the user accepted. */
export async function triggerInstallPrompt(): Promise<boolean> {
  const evt = deferred;
  if (!evt) return false;
  deferred = null; // a captured prompt can only be shown once
  await evt.prompt();
  const { outcome } = await evt.userChoice;
  return outcome === 'accepted';
}

/** Whether the app is already running installed (standalone display mode). */
export function isStandalone(): boolean {
  const nav = navigator as Navigator & { standalone?: boolean };
  return window.matchMedia?.('(display-mode: standalone)').matches || nav.standalone === true;
}

/** Best-effort device family, used to default the install guide to the right tab. */
export function detectPlatform(): Platform {
  const ua = navigator.userAgent;
  // iPadOS 13+ reports as "MacIntel" with a mouse-less touch screen — the
  // standard sniff for telling it apart from an actual Mac.
  const isIOS = /iPad|iPhone|iPod/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  if (isIOS) return 'ios';
  if (/Android/.test(ua)) return 'android';
  return 'desktop';
}
